import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageSquare, ChevronDown } from 'lucide-react';
import { CopyButton } from './CopyButton';
import type { ParsedSocialPost } from '../../types/results';

type CardTone = 'blue' | 'emerald' | 'pink' | 'violet' | 'amber' | 'cyan';

const ACTIVE_TONE: Record<CardTone, string> = {
  blue:    'border-blue-400/60 ring-blue-400/40 shadow-[0_0_32px_rgba(37,99,235,0.35)]',
  emerald: 'border-emerald-400/60 ring-emerald-400/40 shadow-[0_0_32px_rgba(16,185,129,0.35)]',
  pink:    'border-pink-300/70 ring-pink-300/45 shadow-[0_0_32px_rgba(244,143,177,0.4)]',
  violet:  'border-violet-400/60 ring-violet-400/40 shadow-[0_0_32px_rgba(139,92,246,0.35)]',
  amber:   'border-amber-400/60 ring-amber-400/40 shadow-[0_0_32px_rgba(245,158,11,0.35)]',
  cyan:    'border-cyan-400/60 ring-cyan-400/40 shadow-[0_0_32px_rgba(34,211,238,0.35)]',
};

const NUMBER_BADGE: Record<CardTone, string> = {
  blue:    'bg-blue-500/15 text-blue-300',
  emerald: 'bg-emerald-500/15 text-emerald-300',
  pink:    'bg-pink-400/15 text-pink-200',
  violet:  'bg-violet-500/15 text-violet-300',
  amber:   'bg-amber-500/15 text-amber-300',
  cyan:    'bg-cyan-500/15 text-cyan-300',
};

interface SocialPostCardProps {
  post: ParsedSocialPost;
  index: number;
  cardId?: string;
  isActive?: boolean;
  startExpanded?: boolean;
  onHoverChange?: (hovered: boolean) => void;
  tone?: CardTone;
}

export function SocialPostCard({ post, index, cardId, isActive, startExpanded, onHoverChange, tone = 'blue' }: SocialPostCardProps) {
  const [isExpanded, setIsExpanded] = useState(!!startExpanded);

  return (
    <motion.div
      id={cardId}
      onMouseEnter={() => onHoverChange?.(true)}
      onMouseLeave={() => onHoverChange?.(false)}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      className={`rounded-2xl bg-slate-900/50 backdrop-blur-xl overflow-hidden transition-all duration-300 ring-1 scroll-mt-24 ${
        isActive
          ? ACTIVE_TONE[tone]
          : 'border border-slate-700/50 ring-transparent'
      }`}
    >
      {/* Header - clickable to toggle */}
      <button
        type="button"
        onClick={() => setIsExpanded((prev) => !prev)}
        className="flex items-center justify-between w-full p-4 text-left hover:bg-slate-800/30 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold ${NUMBER_BADGE[tone]}`}>
            {post.number}
          </div>
          <div className="flex items-center gap-1.5 text-sm text-slate-400">
            <MessageSquare className="w-3.5 h-3.5" />
            {post.dayLabel}
          </div>
          <span className="text-xs text-slate-500">
            {post.characterCount.toLocaleString()} chars
          </span>
        </div>
        <motion.div
          animate={{ rotate: isExpanded ? 180 : 0 }}
          transition={{ duration: 0.2 }}
        >
          <ChevronDown className="w-5 h-5 text-slate-400" />
        </motion.div>
      </button>

      {/* Collapsible content */}
      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            {/* Copy action */}
            <div className="flex items-center gap-2 px-4 pb-2">
              <CopyButton text={post.content} variant="small" label="Copy" />
            </div>

            {/* Content - preserve line breaks for social posts */}
            <div className="p-4 pt-0">
              <p className="text-slate-300 text-sm whitespace-pre-wrap leading-relaxed">
                {post.content}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
